import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/queryClient'
import { AuthProvider } from './auth/AuthContext'
import { App } from './App'
import { ErrorBoundary } from './components/ErrorBoundary'
import { UpdateToast } from './components/pwa/UpdateToast'
// Import ESTÁTICO y por efecto lateral: 'beforeinstallprompt' se dispara muy
// temprano y una sola vez por carga. Registrado más tarde (o dentro de un
// componente que se monta por página) el evento ya se habría perdido.
import './lib/install'
// Registra el service worker de la PWA en scope '/' (el de FCM vive aparte, en
// /firebase-cloud-messaging-push-scope, para que este no lo desaloje — lib/push.ts)
// y avisa cuando hay versión nueva en vez de actualizar en silencio.
import './lib/swUpdate'
import { instalarCapturaGlobal } from './lib/telemetry'
import { iniciarPersistencia, restaurarCache } from './lib/persist'
import { iniciarFlushEscritorio } from './lib/flush'
import { marcarEscritorio } from './lib/desktop'
import './index.css'

// Se rehidrata ANTES de montar: si se montara primero, las pantallas dispararían sus
// queries con el cache vacío y sin red mostrarían error antes de que llegue el dato
// guardado. restaurarCache nunca lanza (idb.ts degrada a no-op), así que un fallo de
// IndexedDB solo significa arrancar sin cache, no una app rota.
instalarCapturaGlobal()
await restaurarCache()
iniciarPersistencia()
// En la app de escritorio no llegan 'pagehide' ni 'visibilitychange' al cerrar o
// minimizar la ventana: el aviso es window.desktop.onBeforeHide. En navegador es
// un no-op. Va aquí, no en un componente, porque cerrar la app no espera a React.
iniciarFlushEscritorio()
// Marca el <html> con la plataforma del cascarón ANTES del primer render: en Mac la
// barra de camp tiene que dejarle sitio a los semáforos de la ventana (index.css), y
// hacerlo después dejaría un fotograma con el logo tapado. En navegador es un no-op.
marcarEscritorio()
// Tira los borradores de más de una semana para que IndexedDB no crezca sin techo.
// No se espera: es limpieza de fondo y no debe retrasar el primer pintado.
void import('./lib/drafts').then((m) => m.purgarBorradores())

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Fuera de los providers a propósito: si uno de ellos revienta al montar, la
        pantalla de rescate tiene que poder pintarse igual. */}
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
      <UpdateToast />
    </ErrorBoundary>
  </StrictMode>,
)
